import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { tokens } from '../../theme/tokens';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTheme } from '../../contexts/ThemeContext';



const cardSx = {
  backgroundColor: 'rgba(255,255,255,0.95)',
  borderRadius: 3,
  boxShadow: '0 18px 42px rgba(9,32,56,0.18)',
  p: { xs: 2.5, md: 3 },
  backdropFilter: 'blur(6px)'
} as const;

const redColor = tokens.primary;

const normalizeNumber = (value: string): string => value.replace(/,/g, '.');

const numberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});

// HX Diagramm (psychrometrisch): Temperatur (x) vs. absolute Feuchte (y)
// Vereinfachtes SVG mit Achsen, Sättigungskurve (φ=100%) und aktuellem Zustandspunkt
const HXDiagramSVG: React.FC<{ t1?: number; rh1?: number; t2?: number; dark?: boolean }> = ({ t1, rh1, t2, dark = false }) => {
  // Hilfsfunktionen lokal (keine Abhängigkeit vom Component-Scope)
  const satVP = (tC: number) => 6.112 * Math.exp((17.62 * tC) / (243.12 + tC));
  const absHum = (tC: number, rh: number) => 216.7 * ((rh / 100) * satVP(tC)) / (tC + 273.15);

  // Diagramm Bereich
  const T_MIN = 0;
  const T_MAX = 40;
  const AH_MAX = absHum(T_MAX, 100); // max g/m³ bei 40°C

  const width = 600;
  const height = 360;
  const padding = { left: 60, right: 20, top: 30, bottom: 50 };

  const xScale = (t: number) => padding.left + ((t - T_MIN) / (T_MAX - T_MIN)) * (width - padding.left - padding.right);
  const yScale = (ah: number) => height - padding.bottom - (ah / AH_MAX) * (height - padding.top - padding.bottom);

  const pathSaturation = Array.from({ length: T_MAX - T_MIN + 1 }).map((_, i) => {
    const t = T_MIN + i;
    const ah = absHum(t, 100);
    return `${i === 0 ? 'M' : 'L'} ${xScale(t).toFixed(1)} ${yScale(ah).toFixed(1)}`;
  }).join(' ');

  // Isolinien für konstante relative Feuchte
  const isoRHs = [20, 40, 60, 80];
  const isoPaths = isoRHs.map((rh) => ({
    rh,
    d: Array.from({ length: T_MAX - T_MIN + 1 }).map((_, i) => {
      const t = T_MIN + i;
      const ah = absHum(t, rh);
      return `${i === 0 ? 'M' : 'L'} ${xScale(t).toFixed(1)} ${yScale(ah).toFixed(1)}`;
    }).join(' ')
  }));

  const t1Num = Number.isFinite(t1!) ? (t1 as number) : undefined;
  const rh1Num = Number.isFinite(rh1!) ? (rh1 as number) : undefined;
  const t2Num = Number.isFinite(t2!) ? (t2 as number) : undefined;

  const ah1 = t1Num !== undefined && rh1Num !== undefined ? absHum(t1Num, rh1Num) : undefined;
  const ah2sat = t2Num !== undefined ? absHum(t2Num, 100) : undefined;

  const bg = dark ? '#0f172a' : '#fafafa';
  const grid = dark ? '#334155' : '#e2e8f0';
  const axis = dark ? '#cbd5e1' : '#0f172a';
  const isoColor = dark ? '#94a3b8' : '#64748b';

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
      <rect x={0} y={0} width={width} height={height} fill={bg} rx={8} />

      {/* Grid Lines */}
      {Array.from({ length: 9 }).map((_, i) => {
        const t = T_MIN + (i * (T_MAX - T_MIN)) / 8;
        const x = xScale(t);
        return <line key={`vg${i}`} x1={x} y1={padding.top} x2={x} y2={height - padding.bottom} stroke={grid} strokeWidth={1} />;
      })}
      {Array.from({ length: 8 }).map((_, i) => {
        const ah = (i * AH_MAX) / 8;
        const y = yScale(ah);
        return <line key={`hg${i}`} x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke={grid} strokeWidth={1} />;
      })}

      {/* Axes */}
      <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke={axis} strokeWidth={2} />
      <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke={axis} strokeWidth={2} />
      <text x={(width - padding.right + padding.left) / 2} y={height - 12} fill={axis} fontSize={12} textAnchor="middle">Temperatur [°C]</text>
      <text x={20} y={(height - padding.bottom + padding.top) / 2} fill={axis} fontSize={12} transform={`rotate(-90 20 ${(height - padding.bottom + padding.top) / 2})`} textAnchor="middle">Absolute Feuchte [g/m³]</text>

      {/* Axis ticks */}
      {Array.from({ length: 9 }).map((_, i) => {
        const t = T_MIN + (i * (T_MAX - T_MIN)) / 8;
        const x = xScale(t);
        return <text key={`xt${i}`} x={x} y={height - padding.bottom + 18} fill={axis} fontSize={11} textAnchor="middle">{Math.round(t)}</text>;
      })}
      {Array.from({ length: 9 }).map((_, i) => {
        const ah = (i * AH_MAX) / 8;
        const y = yScale(ah);
        return <text key={`yt${i}`} x={padding.left - 8} y={y + 4} fill={axis} fontSize={11} textAnchor="end">{Math.round(ah)}</text>;
      })}

      {/* Isolinien für φ */}
      {isoPaths.map(({ rh, d }) => (
        <g key={`iso-${rh}`}>
          <path d={d} fill="none" stroke={isoColor} strokeWidth={1} strokeDasharray="3 3" />
          <text x={xScale(T_MAX) - 5} y={yScale(absHum(T_MAX, rh)) - 4} fill={isoColor} fontSize={10} textAnchor="end">φ={rh}%</text>
        </g>
      ))}

      {/* Sättigungskurve φ=100% */}
      <path d={pathSaturation} fill="none" stroke={dark ? '#60a5fa' : '#1d4ed8'} strokeWidth={2} />
      <text x={xScale(T_MAX) - 80} y={yScale(absHum(T_MAX, 100)) - 10} fill={dark ? '#93c5fd' : '#1e3a8a'} fontSize={11}>Sättigung φ=100%</text>

      {/* Current state point */}
      {t1Num !== undefined && ah1 !== undefined && (
        <g>
          <circle cx={xScale(t1Num)} cy={yScale(ah1)} r={5} fill={dark ? '#22c55e' : '#16a34a'} stroke="#0f172a" strokeWidth={1}>
            <title>{`Zustand: T=${numberFormatter.format(t1Num)}°C, φ=${rh1Num}% → ${numberFormatter.format(ah1)} g/m³`}</title>
          </circle>
          <text x={xScale(t1Num) + 8} y={yScale(ah1) - 8} fill={axis} fontSize={11}>{`${numberFormatter.format(t1Num)}°C, ${numberFormatter.format(ah1)} g/m³`}</text>
        </g>
      )}

      {/* Cooling line to T2 saturation */}
      {t1Num !== undefined && ah1 !== undefined && t2Num !== undefined && ah2sat !== undefined && (
        <g>
          <line x1={xScale(t1Num)} y1={yScale(ah1)} x2={xScale(t2Num)} y2={yScale(ah2sat)} stroke={dark ? '#f59e0b' : '#b45309'} strokeWidth={2} strokeDasharray="4 4" />
          <circle cx={xScale(t2Num)} cy={yScale(ah2sat)} r={4} fill={dark ? '#f59e0b' : '#b45309'}>
            <title>{`Ziel Sättigung: T₂=${numberFormatter.format(t2Num)}°C, φ=100% → ${numberFormatter.format(ah2sat)} g/m³`}</title>
          </circle>
          <text x={xScale(t2Num) + 8} y={yScale(ah2sat) - 8} fill={axis} fontSize={11}>{`${numberFormatter.format(t2Num)}°C, ${numberFormatter.format(ah2sat)} g/m³ (Sätt.)`}</text>
        </g>
      )}
    </svg>
  );
};

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewTaupunkt } = useAuth();
  const [temperature, setTemperature] = useState<string>('');
  const [humidity, setHumidity] = useState<string>('');
  const [result, setResult] = useState<string>('Ergebnis wird hier angezeigt');
  const [errors, setErrors] = useState<{ temperature?: string; humidity?: string }>({});
  const [sensorStatus, setSensorStatus] = useState<string>('Sensorstatus wird geladen...');
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  // Kondensat-States
  const [condTargetTemp, setCondTargetTemp] = useState<string>('10');
  const [condRoomHeight, setCondRoomHeight] = useState<string>('2.5');
  const [condResult, setCondResult] = useState<string>('');

  useEffect(() => {
    setSensorStatus('Umgebungstemp.-Sensor: Nicht verfügbar im Browser Luftfeuchte-Sensor: Nicht verfügbar');
  }, []);

  const resetErrors = useCallback(() => setErrors({}), []);

  const calculateDewPoint = useCallback(
    (mode: 'auto' | 'manual' = 'auto') => {
      const tempTrimmed = temperature.trim();
      const humidityTrimmed = humidity.trim();

      if (!tempTrimmed || !humidityTrimmed) {
        if (mode === 'manual') {
          setResult('Bitte Temperatur und Feuchtigkeit eingeben.');
          setErrors(prev => ({
            temperature: !tempTrimmed ? 'Pflichtfeld' : prev.temperature,
            humidity: !humidityTrimmed ? 'Pflichtfeld' : prev.humidity
          }));
        }
        return;
      }

      const tempValue = Number(normalizeNumber(tempTrimmed));
      const humidityValue = Number(normalizeNumber(humidityTrimmed));

      if (!Number.isFinite(tempValue)) {
        if (mode === 'manual') {
          setErrors(prev => ({ ...prev, temperature: 'Ungültige Zahl.' }));
          setResult('Ungültige Eingabe.');
        }
        return;
      }

      if (!Number.isFinite(humidityValue)) {
        if (mode === 'manual') {
          setErrors(prev => ({ ...prev, humidity: 'Ungültige Zahl.' }));
          setResult('Ungültige Eingabe.');
        }
        return;
      }

      if (humidityValue < 0 || humidityValue > 100) {
        setErrors(prev => ({ ...prev, humidity: '0-100%' }));
        setResult('Feuchtigkeit muss zwischen 0 und 100% liegen.');
        return;
      }

      resetErrors();
      const a = 17.62;
      const b = 243.12;
      const alpha = (a * tempValue) / (b + tempValue) + Math.log(humidityValue / 100);
      const dewPoint = (b * alpha) / (a - alpha);

      if (Number.isFinite(dewPoint)) {
        setResult(`Taupunkt: ${numberFormatter.format(dewPoint)} °C`);
      } else {
        setResult('Berechnung nicht möglich (ungültige Werte).');
      }
    },
    [humidity, resetErrors, temperature]
  );

  useEffect(() => {
    if (!temperature.trim() && !humidity.trim()) {
      setResult('Ergebnis wird hier angezeigt');
      resetErrors();
      return;
    }
    calculateDewPoint('auto');
  }, [calculateDewPoint, humidity, resetErrors, temperature]);

  const helperTexts = useMemo(
    () => ({
      temperature: errors.temperature || 'Temperatur in °C (optional Sensorwerte übernehmen)',
      humidity: errors.humidity || 'Relative Luftfeuchte in %'
    }),
    [errors]
  );

  // Hilfsfunktionen: Sättigungsdampfdruck (hPa) nach Magnus, absolute Feuchte (g/m³)
  const saturationVaporPressure = (tC: number) => 6.112 * Math.exp((17.62 * tC) / (243.12 + tC)); // hPa
  const absoluteHumidity = (tC: number, rh: number) => {
    const es = saturationVaporPressure(tC); // hPa
    const pv = (rh / 100) * es; // hPa
    // 216.7 nutzt hPa → Ergebnis in g/m³
    return 216.7 * (pv / (tC + 273.15));
  };

  // Aktionen (Header): Reset, Ergebnis kopieren
  const actionButtons = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip title="Alle Eingaben zurücksetzen">
        <IconButton
          color="inherit"
          onClick={() => {
            setTemperature('');
            setHumidity('');
            setResult('Ergebnis wird hier angezeigt');
            setErrors({});
            setSnackbar({ message: 'Zurückgesetzt.', severity: 'info' });
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Ergebnis kopieren">
        <span>
          <IconButton
            color="inherit"
            disabled={!result || result === 'Ergebnis wird hier angezeigt'}
            onClick={() => {
              navigator.clipboard.writeText(result);
              setSnackbar({ message: 'Ergebnis kopiert.', severity: 'success' });
            }}
          >
            <ContentCopyIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  if (!canViewTaupunkt) {
    return (
      <UnitPageShell title="M99 · Taupunktberechnung" showTTS={false}>
        <Alert severity="error">Keine Berechtigung fuer den Taupunktrechner.</Alert>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell title="M99 · Taupunktberechnung" showTTS={false} actions={actionButtons}>
      <Box sx={{ maxWidth: 600, mx: 'auto', width: '100%' }}>
        <Paper sx={cardSx}>
          <Stack spacing={2.5}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: redColor }}>
              Eingabewerte
            </Typography>

            <TextField
              label="Temperatur (°C)"
              value={temperature}
              onChange={event => setTemperature(event.target.value)}
              error={Boolean(errors.temperature)}
              helperText={helperTexts.temperature}
              inputMode="decimal"
              fullWidth
            />

            <TextField
              label="Relative Luftfeuchtigkeit (%)"
              value={humidity}
              onChange={event => setHumidity(event.target.value)}
              error={Boolean(errors.humidity)}
              helperText={helperTexts.humidity}
              inputMode="decimal"
              fullWidth
            />

            <Button
              variant="outlined"
              onClick={() => calculateDewPoint('manual')}
              sx={{
                borderColor: redColor,
                color: redColor,
                '&:hover': { borderColor: tokens.colors.primaryHover, color: tokens.colors.primaryHover }
              }}
            >
              Taupunkt berechnen
            </Button>

            <Typography variant="h5" sx={{ color: redColor, fontWeight: 700, textAlign: 'center' }}>
              {result}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-line',
                color: '#1B873B',
                borderRadius: 2,
                border: '1px dashed rgba(27,135,59,0.5)',
                backgroundColor: 'rgba(229,246,233,0.6)',
                p: 1.5,
                textAlign: 'center'
              }}
            >
              {sensorStatus}
            </Typography>

            {/* HX Diagramm */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                HX-Diagramm (psychrometrisch)
              </Typography>
              <Box sx={{ width: '100%', maxWidth: 700, mx: 'auto' }}>
                <HXDiagramSVG
                  t1={temperature.trim() ? Number(normalizeNumber(temperature.trim())) : undefined}
                  rh1={humidity.trim() ? Number(normalizeNumber(humidity.trim())) : undefined}
                  t2={condTargetTemp.trim() ? Number(normalizeNumber(condTargetTemp.trim())) : undefined}
                  dark={darkMode}
                />
              </Box>
            </Box>
          </Stack>
        </Paper>

        {/* Karte: Kondensat-Berechnung bei Abkühlung */}
        <Paper sx={{ ...cardSx, mt: 3 }}>
          <Stack spacing={2.5}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: redColor }}>
              Kondensat bei Abkühlung (ml/m²)
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Beispiel: 28°C Luft mit 80% rF wird auf 10°C abgekühlt. Wie viel Wasser kondensiert?
            </Typography>

            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Ausgangstemperatur T₁ (°C)"
                  inputMode="decimal"
                  value={temperature}
                  onChange={e => setTemperature(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Relative Feuchte φ₁ (%)"
                  inputMode="decimal"
                  value={humidity}
                  onChange={e => setHumidity(e.target.value)}
                  fullWidth
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Zieltemperatur T₂ (°C)"
                  inputMode="decimal"
                  value={condTargetTemp}
                  onChange={e => setCondTargetTemp(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Raumhöhe h (m) für ml/m²"
                  inputMode="decimal"
                  value={condRoomHeight}
                  onChange={e => setCondRoomHeight(e.target.value)}
                  fullWidth
                />
              </Stack>

              <Button
                variant="outlined"
                onClick={() => {
                  const t1 = Number(normalizeNumber(temperature.trim()));
                  const rh1 = Number(normalizeNumber(humidity.trim()));
                  const t2 = Number(normalizeNumber(condTargetTemp.trim()));
                  const h = Number(normalizeNumber(condRoomHeight.trim()));

                  if (!Number.isFinite(t1) || !Number.isFinite(rh1) || !Number.isFinite(t2) || !Number.isFinite(h)) {
                    setSnackbar({ message: 'Bitte gültige Zahlen eingeben.', severity: 'error' });
                    return;
                  }
                  if (rh1 < 0 || rh1 > 100) {
                    setSnackbar({ message: 'Relative Feuchte muss zwischen 0 und 100% liegen.', severity: 'error' });
                    return;
                  }

                  // Absolute Feuchte vor Abkühlung (g/m³)
                  const ah1 = absoluteHumidity(t1, rh1);
                  // Max. absolute Feuchte bei Zieltemperatur (Sättigung, φ=100%)
                  const ah2_sat = absoluteHumidity(t2, 100);

                  const delta_g_per_m3 = Math.max(0, ah1 - ah2_sat); // g/m³, kondensiert
                  const ml_per_m2 = delta_g_per_m3 * Math.max(0, h); // g ≈ ml, pro m² bei Raumhöhe h

                  setCondResult(
                    `Kondensat: ${numberFormatter.format(ml_per_m2)} ml/m² (Δ=${numberFormatter.format(delta_g_per_m3)} g/m³, h=${h} m)`
                  );
                }}
                sx={{ borderColor: redColor, color: redColor, '&:hover': { borderColor: tokens.colors.primaryHover, color: tokens.colors.primaryHover } }}
              >
                Kondensat berechnen
              </Button>

              <Typography variant="h6" sx={{ color: redColor, fontWeight: 700, textAlign: 'center' }}>
                {condResult || 'Ergebnis wird hier angezeigt'}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Annahme: Umrechnung g→ml und Volumen pro m² über Höhe h (ml/m² = g/m³ × h).
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        <Snackbar
          open={Boolean(snackbar)}
          autoHideDuration={3000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {snackbar ? (
            <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
              {snackbar.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </Box>
    </UnitPageShell>
  );
};

export default Page;
